import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import InfoHint from './InfoHint';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, Filter, ChevronDown, ChevronUp, Eye, Download, X } from 'lucide-react';
import { fetchCallFilterOptions, fetchCalls } from '../api';
import { useExportJobs } from './exports/ExportJobsContext';
import { PANEL_IDS } from '../config/panelRegistry';
import { usePanelSettings } from '../contexts/PanelSettingsContext';
import { useCallCardFields } from '../contexts/CallCardFieldsContext';
import useEmployees from '../hooks/useEmployees';
import useNodeCatalog from '../hooks/useNodeCatalog';
import NodeFiltersPanel from './nodes/NodeFiltersPanel';
import {
  MAX_NODE_FILTERS,
  buildNodeFilterEntries,
  parseNodeFilterEntries,
  selectNodeCatalogFilters
} from './nodes/nodeFilterUtils';
import {
  EMPTY_EMPLOYEE_SCOPE,
  buildCallFilterParams,
  describeScopeDepartments,
  describeScopeEmployees,
  readEmployeeScope
} from './callDetailsFilters';
import {
  NODE_CALL_OUTCOME_CODES,
  resolveCallOutcome,
  resolveCallTypeLabel,
  resolveChecklistScore,
  resolveComplaints
} from '../utils/callNodeFields';
import CallDetailDialog from './calls/CallDetailDialog';
import { CALL_CARD_FIELD_SLOTS, journalColumnCount } from './calls/callCardFields';
import { formatDate, formatDuration } from './calls/callDetailFormat';
import { formatCallDirection } from '../utils/labels';
import { useIsMobile } from '../hooks/useMediaQuery';

const PAGE_SIZE = 25;

// Досортировка текущей страницы поверх серверной. Значения, собираемые из нескольких
// источников, берём тем же резолвером, что и рендер, иначе порядок разойдётся с видимым.
const SORT_VALUE_RESOLVERS = {
  checklistScore: resolveChecklistScore
};

const CRITERION_LABELS = {
  answered_question: 'Ответил на вопрос клиента',
  identified_need: 'Выявил потребность',
  fixed_next_step: 'Зафиксировал следующий шаг',
  clear_closing: 'Корректно завершил разговор',
  no_forbidden_phrases: 'Не использовал запрещенные фразы',
  found_order_or_context: 'Нашел заявку/контекст',
  communicated_status_or_time: 'Сообщил статус или сроки',
  confirmed_agreements: 'Подтвердил договоренности',
  clarified_problem: 'Уточнил проблему',
  gave_instruction_or_solution: 'Дал инструкцию или решение',
  offered_service_next_step: 'Предложил сервисный следующий шаг',
  no_internal_problem: 'Не раскрывал внутренние проблемы'
};

const CallDetails = ({ dateRange, callType = 'all', initialCallId, onInitialCallHandled, initialFilters, onInitialFiltersHandled }) => {
  const panels = usePanelSettings();
  // Колонка «Итог звонка» — то же поле, что в карточке звонка: название и видимость общие.
  const cardFields = useCallCardFields();
  const outcomeVisible = cardFields.isVisible(CALL_CARD_FIELD_SLOTS.OUTCOME);
  const columnCount = journalColumnCount({ outcomeVisible });
  // Плейсхолдер поиска — не панель: он живёт внутри карточки фильтров и в реестр не входит.
  const searchPlaceholder = 'Поиск по содержимому...';
  const { employees } = useEmployees();
  const { startExport } = useExportJobs();

  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  // Спец-фильтр drill-down с вкладки «Сотрудники»: своего select'а нет, снимается чипом.
  const [criterionFilter, setCriterionFilter] = useState(null); // { criterion, maxScore }
  // Срез раздела «Сотрудники»: списки сотрудников и отделов. Тоже без селектов и тоже
  // снимается чипами — почему не в селектах, см. readEmployeeScope.
  const [employeeScope, setEmployeeScope] = useState(EMPTY_EMPLOYEE_SCOPE);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [selectedCall, setSelectedCall] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const isMobile = useIsMobile();

  const [filterOptions, setFilterOptions] = useState(null);

  // ── Фильтры по результатам анализа ──────────────────────────────────────────
  // Состав контролов приходит из каталога активного пакета, поэтому новый пакет
  // появляется в журнале без правок кода — ровно как на дашборде.
  const { catalog, isLoading: isCatalogLoading } = useNodeCatalog();
  const [nodeFilterValues, setNodeFilterValues] = useState({});
  // Записи, которые каталог контролом не выражает (скрытая карточка, перезалитый пакет).
  // Их нельзя терять: иначе журнал показал бы больше звонков, чем карточка, с которой пришли.
  const [extraNodeEntries, setExtraNodeEntries] = useState([]);
  // Сырая строка из drill-down: пока каталог не приехал, она уходит на сервер как есть.
  const [pendingNodeFilter, setPendingNodeFilter] = useState(null);

  const nodeCatalogFilters = useMemo(() => selectNodeCatalogFilters(catalog), [catalog]);

  const nodeFilterEntries = useMemo(() => {
    if (pendingNodeFilter !== null) {
      return pendingNodeFilter.split(';').filter(Boolean);
    }

    return [...buildNodeFilterEntries(nodeFilterValues, nodeCatalogFilters), ...extraNodeEntries];
  }, [pendingNodeFilter, nodeFilterValues, extraNodeEntries, nodeCatalogFilters]);

  const nodeFilter = nodeFilterEntries.length > 0
    ? nodeFilterEntries.slice(0, MAX_NODE_FILTERS).join(';')
    : undefined;

  // Разбор нужен только чтобы показать условие контролами. Благодаря гарантии round-trip
  // (см. nodeFilterUtils) строка после разбора совпадает с исходной, поэтому переключение
  // не порождает второго, другого запроса.
  useEffect(() => {
    if (pendingNodeFilter === null || isCatalogLoading) {
      return;
    }

    const { values, unmatched } = parseNodeFilterEntries(pendingNodeFilter, nodeCatalogFilters);
    setNodeFilterValues(values);
    setExtraNodeEntries(unmatched);
    setPendingNodeFilter(null);
  }, [pendingNodeFilter, isCatalogLoading, nodeCatalogFilters]);

  const handleNodeFilterChange = useCallback((id, value) => {
    setNodeFilterValues((current) => ({ ...current, [id]: value }));
  }, []);

  const resetNodeFilters = useCallback(() => {
    setNodeFilterValues({});
    setExtraNodeEntries([]);
  }, []);

  const buildFilterParams = useCallback(() => buildCallFilterParams({
    department: selectedDepartment,
    employeeId: selectedEmployee,
    employeeScope,
    callType,
    search: searchQuery,
    dateRange,
    nodeFilter,
    criterionFilter
  }), [
    selectedDepartment,
    selectedEmployee,
    employeeScope,
    callType,
    searchQuery,
    dateRange,
    nodeFilter,
    criterionFilter
  ]);

  // Ответы нумеруются, потому что на монтировании запросов уходит два: первый — до того,
  // как применился пришедший из графика фильтр. Без счётчика выигрывал тот, что вернулся
  // последним, и журнал случайным образом оказывался нефильтрованным.
  const requestSeq = useRef(0);

  const loadCalls = useCallback(async () => {
    const seq = ++requestSeq.current;
    setIsLoading(true);
    setError(null);

    try {
      const params = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        ...buildFilterParams(),
        sort: sortConfig.key ?? undefined,
        order: sortConfig.key ? sortConfig.direction : undefined
      };

      const response = await fetchCalls(params);
      if (seq !== requestSeq.current) {
        return;
      }

      setCalls(response.items ?? []);
      setTotal(response.total ?? 0);
    } catch (err) {
      if (seq !== requestSeq.current) {
        return;
      }

      console.error(err);
      setError('Не удалось загрузить список звонков.');
    } finally {
      if (seq === requestSeq.current) {
        setIsLoading(false);
      }
    }
  }, [page, buildFilterParams, sortConfig]);

  useEffect(() => {
    setPage(1);
  }, [
    dateRange?.from,
    dateRange?.to,
    callType,
    selectedDepartment,
    selectedEmployee,
    employeeScope,
    criterionFilter,
    nodeFilter
  ]);

  // Drill-down с других вкладок описывает состояние журнала целиком: всё, чем владеет
  // переход, сбрасывается, а не доливается. Иначе два клика подряд давали пересечение двух
  // срезов, которого пользователь не выбирал, — и без единого видимого признака.
  useEffect(() => {
    if (!initialFilters) {
      return;
    }

    setCriterionFilter(
      initialFilters.criterion
        ? { criterion: initialFilters.criterion, maxScore: initialFilters.criterion_max_score ?? 0.5 }
        : null
    );
    setSelectedEmployee(initialFilters.employee_id ?? 'all');
    setEmployeeScope(readEmployeeScope(initialFilters));
    setSearchQuery(initialFilters.search ?? '');

    setNodeFilterValues({});
    setExtraNodeEntries([]);
    setPendingNodeFilter(initialFilters.node_filter ?? null);

    setPage(1);
    onInitialFiltersHandled?.();
  }, [initialFilters, onInitialFiltersHandled]);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  // Эндпоинт остался источником только тех списков, что стоят на реальных колонках `calls`:
  // сотрудники и отделы. Направление из верхней панели сужает список сотрудников; свой
  // список направлений в ответе журналу больше не нужен. Разрезы анализа — из каталога узлов.
  useEffect(() => {
    if (!dateRange?.from || !dateRange?.to) {
      setFilterOptions(null);
      return undefined;
    }

    let cancelled = false;

    const loadFilterOptions = async () => {
      try {
        const response = await fetchCallFilterOptions({
          date_from: format(dateRange.from, 'yyyy-MM-dd'),
          date_to: format(dateRange.to, 'yyyy-MM-dd'),
          call_type: callType === 'all' ? undefined : callType
        });

        if (!cancelled) {
          setFilterOptions(response ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
        }
      }
    };

    loadFilterOptions();

    return () => {
      cancelled = true;
    };
  }, [dateRange?.from, dateRange?.to, callType]);

  useEffect(() => {
    if (!initialCallId) {
      return;
    }

    let cancelled = false;

    fetchCalls({
      call_id: initialCallId,
      limit: 1
    })
      .then((response) => {
        if (cancelled) {
          return;
        }

        const call = response.items?.[0];
        if (call) {
          setSelectedCall(call);
          setShowDetailsModal(true);
          return;
        }

        setError('Звонок по ссылке не найден.');
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          onInitialCallHandled?.();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [initialCallId, onInitialCallHandled]);

  const handleExport = useCallback(() => {
    startExport(buildFilterParams());
  }, [buildFilterParams, startExport]);

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.key === column) {
        return {
          key: column,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }

      return { key: column, direction: 'asc' };
    });
  };

  const sortedCalls = useMemo(() => {
    if (!sortConfig.key) {
      return calls;
    }

    const key = sortConfig.key;
    const direction = sortConfig.direction === 'asc' ? 1 : -1;

    return [...calls].sort((a, b) => {
      const resolver = SORT_VALUE_RESOLVERS[key];
      const aVal = resolver ? resolver(a) : a[key];
      const bVal = resolver ? resolver(b) : b[key];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * direction;
      }
      return String(aVal).localeCompare(String(bVal)) * direction;
    });
  }, [calls, sortConfig]);

  const employeeOptions = useMemo(() => {
    const apiEmployees = (filterOptions?.employees ?? [])
      .map((employee) => ({
        id: employee.code?.toString?.() ?? employee.id?.toString?.() ?? '',
        name: employee.label ?? employee.name ?? employee.fullName ?? employee.code,
        department: employee.department ?? null
      }))
      .filter((employee) => employee.id);

    if (apiEmployees.length > 0) {
      return apiEmployees;
    }

    return employees
      .map((employee) => ({
        id: employee.id?.toString?.() ?? employee.employeeId?.toString?.() ?? '',
        name: employee.name ?? employee.fullName ?? employee.employeeName ?? 'Без имени',
        department: employee.department ?? null
      }))
      .filter((employee) => employee.id);
  }, [employees, filterOptions?.employees]);

  const departmentOptions = useMemo(() => {
    const apiDepartments = (filterOptions?.departments ?? [])
      .map((department) => ({
        id: department.code?.toString?.() ?? department.label?.toString?.() ?? '',
        name: department.label ?? department.code
      }))
      .filter((department) => department.id);
    const departments = apiDepartments.length > 0
      ? apiDepartments
      : [...new Set(employeeOptions.filter((employee) => employee.department).map((employee) => employee.department))]
      .sort((a, b) => a.localeCompare(b, 'ru', { sensitivity: 'base' }))
      .map((department) => ({ id: department, name: department }));

    return [
      { id: 'all', name: 'Все отделы' },
      ...departments
    ];
  }, [employeeOptions, filterOptions?.departments]);

  const uniqueEmployees = useMemo(() => {
    const filteredEmployees = selectedDepartment === 'all'
      ? employeeOptions
      : employeeOptions.filter((employee) => employee.department === selectedDepartment);

    return [
      { id: 'all', name: 'Все сотрудники' },
      ...filteredEmployees.map((emp) => ({
        id: emp.id,
        name: emp.name
      }))
    ];
  }, [employeeOptions, selectedDepartment]);

  useEffect(() => {
    if (selectedDepartment !== 'all' && !departmentOptions.some((department) => department.id === selectedDepartment)) {
      setSelectedDepartment('all');
    }
  }, [departmentOptions, selectedDepartment]);

  useEffect(() => {
    // Пока справочник сотрудников не загрузился, в списке лежит только пункт «Все
    // сотрудники», и сбрасывать по нему выбор нельзя: переход из графика приходит
    // с id раньше, чем приедет список, и фильтр молча слетал на «все» — журнал
    // открывался по всем звонкам, хотя кликали по конкретному оператору.
    if (uniqueEmployees.length <= 1) {
      return;
    }

    if (selectedEmployee !== 'all' && !uniqueEmployees.some((employee) => employee.id === selectedEmployee)) {
      setSelectedEmployee('all');
    }
  }, [selectedEmployee, uniqueEmployees]);

  // Имена для чипа среза: справочник плюс список журнала за период. Только список журнала
  // не годится — в нём нет тех, у кого за период нет звонков, а «Сотрудники» их выбирают.
  const employeeNameById = useMemo(() => {
    const names = new Map();
    [...employees, ...(filterOptions?.employees ?? [])].forEach((employee) => {
      const id = employee.id?.toString?.() ?? employee.code?.toString?.();
      const name = employee.name ?? employee.fullName ?? employee.label;
      if (id && name && !names.has(id)) {
        names.set(id, name);
      }
    });
    return names;
  }, [employees, filterOptions?.employees]);

  const handleDepartmentChange = useCallback((department) => {
    setSelectedDepartment(department);
    setSelectedEmployee('all');
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasEmployeeScope = employeeScope.departments.length > 0 || employeeScope.employeeIds.length > 0;

  return (
    <div className="space-y-6">
      <Card data-panel-id={PANEL_IDS['ui:calls.header']}>
        <CardHeader className="space-y-3">
          <div className="text-left">
            <div className="flex items-center gap-1.5">
              <CardTitle>{panels.title(PANEL_IDS['ui:calls.header'])}</CardTitle>
              <InfoHint text={panels.description(PANEL_IDS['ui:calls.header'])} label="О разделе" trackId="calls.header.hint" />
            </div>
          </div>
          {/* Направления здесь нет: оно в верхней панели, общей для разделов. Свой селект
              у журнала был вторым контролом того же смысла и о первом не знал. */}
          <div className="grid w-full gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-5">
            <div className="relative w-full">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                data-ev="calls.filters.search"
                className="pl-9"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(event) => {
                  setPage(1);
                  setSearchQuery(event.target.value);
                }}
              />
            </div>

            <Select trackId="calls.filters.department" value={selectedDepartment} onValueChange={handleDepartmentChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Все отделы" />
              </SelectTrigger>
              <SelectContent>
                {departmentOptions.map((department) => (
                  <SelectItem key={department.id} value={department.id}>
                    {department.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select trackId="calls.filters.employee" value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Сотрудник" />
              </SelectTrigger>
              <SelectContent>
                {uniqueEmployees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" className="w-full" data-ev="calls.filters.refresh" onClick={loadCalls}>
              <Filter className="mr-2 h-4 w-4" />
              Обновить
            </Button>

            <Button variant="outline" className="w-full" data-ev="calls.export" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Excel
            </Button>
          </div>
          {(criterionFilter || extraNodeEntries.length > 0 || hasEmployeeScope) && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="text-xs text-muted-foreground">Доп. фильтры:</span>
              {employeeScope.departments.length > 0 && (
                <FilterChip
                  label={describeScopeDepartments(employeeScope.departments)}
                  onRemove={() => setEmployeeScope((current) => ({ ...current, departments: [] }))}
                />
              )}
              {employeeScope.employeeIds.length > 0 && (
                <FilterChip
                  label={describeScopeEmployees(employeeScope.employeeIds, employeeNameById)}
                  onRemove={() => setEmployeeScope((current) => ({ ...current, employeeIds: [] }))}
                />
              )}
              {criterionFilter && (
                <FilterChip
                  label={`Критерий: ${CRITERION_LABELS[criterionFilter.criterion] ?? criterionFilter.criterion}${criterionFilter.maxScore != null ? ` (оценка ≤ ${criterionFilter.maxScore})` : ''}`}
                  onRemove={() => setCriterionFilter(null)}
                />
              )}
              {/* Условие по узлам, которому в каталоге не нашлось контрола: карточку скрыли
                  или пакет перезалили. Чип нужен, чтобы такой фильтр было видно и можно
                  было снять — иначе он молча сужал бы журнал. */}
              {extraNodeEntries.length > 0 && (
                <FilterChip
                  label={`Условие по узлам: ${extraNodeEntries.length}`}
                  onRemove={() => setExtraNodeEntries([])}
                />
              )}
            </div>
          )}
        </CardHeader>
      </Card>

      {nodeCatalogFilters.length > 0 && (
        <NodeFiltersPanel
          filters={nodeCatalogFilters}
          values={nodeFilterValues}
          onChange={handleNodeFilterChange}
          onReset={resetNodeFilters}
          packName={catalog.packName}
          packVersion={catalog.packVersion}
          entryCount={nodeFilterEntries.length}
        />
      )}

      {panels.isVisible(PANEL_IDS['ui:calls.journal']) && (
      <Card data-panel-id={PANEL_IDS['ui:calls.journal']}>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <CardTitle>{panels.title(PANEL_IDS['ui:calls.journal'])}</CardTitle>
          <div className="text-sm text-muted-foreground">
            Страница {page} из {totalPages} &middot; Всего {total.toLocaleString()}
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead label="Дата" column="callStartedAt" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="Сотрудник" column="employeeName" sortConfig={sortConfig} onSort={handleSort} />
                  <TableHead>Клиент</TableHead>
                  {/* На телефоне остаются дата, сотрудник, клиент и итог; остальное есть в
                      карточке звонка, которая открывается касанием строки. */}
                  <TableHead className="max-md:hidden">Направление</TableHead>
                  <TableHead className="max-md:hidden">Тип</TableHead>
                  <TableHead className="max-md:hidden">Жалобы</TableHead>
                  {outcomeVisible && (
                    <TableHead data-call-card-field={CALL_CARD_FIELD_SLOTS.OUTCOME}>
                      {cardFields.title(CALL_CARD_FIELD_SLOTS.OUTCOME)}
                    </TableHead>
                  )}
                  <SortableHead label="Чек-лист" column="checklistScore" sortConfig={sortConfig} onSort={handleSort} numeric className="max-md:hidden" />
                  <SortableHead label="Длительность" column="durationSeconds" sortConfig={sortConfig} onSort={handleSort} numeric className="max-md:hidden" />
                  <TableHead className="w-10 max-sm:hidden"><span className="sr-only">Подробнее</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={columnCount} className="text-center text-muted-foreground">
                      Загружаем данные...
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && sortedCalls.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columnCount} className="text-center text-muted-foreground">
                      Нет записей для отображения.
                    </TableCell>
                  </TableRow>
                )}
                {sortedCalls.map((call) => (
                  <TableRow
                    key={call.callId}
                    data-call-id={call.callId}
                    data-ev={isMobile ? 'calls.row.open' : undefined}
                    className={`[&>td]:align-middle [&>td]:py-3 ${isMobile ? 'cursor-pointer' : ''}`}
                    // На телефоне кнопка «Подробнее» оказалась бы за правым краем таблицы,
                    // поэтому карточку открывает касание строки. На десктопе поведение прежнее.
                    onClick={isMobile ? () => { setSelectedCall(call); setShowDetailsModal(true); } : undefined}
                  >
                    <TableCell className="whitespace-nowrap">{formatDate(call.callStartedAt)}</TableCell>
                    <TableCell className="whitespace-nowrap">{call.employeeName || '—'}</TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{call.clientName || call.clientPhone || '—'}</div>
                      <div className="text-xs text-muted-foreground">{call.clientCompany || (call.clientName ? call.clientPhone : null)}</div>
                    </TableCell>
                    <TableCell className="max-md:hidden">{formatCallDirection(call.callType)}</TableCell>
                    <TableCell className="max-md:hidden">{renderPrimaryIntent(call)}</TableCell>
                    <TableCell className="max-md:hidden">{resolveComplaints(call)}</TableCell>
                    {outcomeVisible && (
                      <TableCell className="min-w-[9.5rem]">
                        <BookingOutcomeBadge call={call} />
                      </TableCell>
                    )}
                    <TableCell numeric className="max-md:hidden">{formatChecklistScore(resolveChecklistScore(call))}</TableCell>
                    <TableCell numeric className="max-md:hidden">{formatDuration(call.durationSeconds)}</TableCell>
                    <TableCell className="max-sm:hidden">
                      <Button size="icon" variant="ghost" data-ev="calls.row.details" onClick={() => { setSelectedCall(call); setShowDetailsModal(true); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
            <span>
              Показано {(page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, total)} из {total.toLocaleString()}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                data-ev="calls.pagination.prev"
                disabled={page === 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                Назад
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-ev="calls.pagination.next"
                disabled={page >= totalPages}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              >
                Вперёд
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      <CallDetailDialog
        call={selectedCall}
        open={showDetailsModal}
        onOpenChange={setShowDetailsModal}
      />
    </div>
  );
};

const SortableHead = ({ label, column, sortConfig, onSort, numeric, className = '' }) => {
  const isActive = sortConfig.key === column;
  return (
    <TableHead numeric={numeric} className={`cursor-pointer select-none whitespace-nowrap ${className}`} data-ev="calls.sort" data-ev-value={column} onClick={() => onSort(column)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />) : null}
      </span>
    </TableHead>
  );
};

const formatChecklistScore = (value) => {
  if (value === null || value === undefined) return '—';
  const parsed = Number(value);
  return Number.isNaN(parsed) ? '—' : parsed.toFixed(1).replace('.', ',');
};

const renderPrimaryIntent = (call) => {
  const label = resolveCallTypeLabel(call);
  if (!label) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  // Коды call_type задаёт пакет тенанта, поэтому цвет не назначаем — только подсказку
  // с обоснованием модели, если нода его вернула. Плашка без цвета ничего не кодирует: обычный текст.
  return <span title={call?.nodeCallTypeReason ?? undefined}>{label}</span>;
};

const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-xs text-foreground">
    {label}
    <button
      type="button"
      data-ev="calls.filters.chip_remove"
      onClick={onRemove}
      className="rounded-full p-0.5 hover:bg-background"
      // Чипов бывает несколько (отделы, сотрудники, критерий), и одинаковые подписи кнопок
      // для скринридера неразличимы.
      aria-label={`Убрать фильтр «${label}»`}
    >
      <X className="h-3 w-3" />
    </button>
  </span>
);

const NEUTRAL_BADGE_CLASS = 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';

const BOOKING_OUTCOME_BADGE_CLASSES = {
  booked: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
  waitlist: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-300',
  refusal: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  refused: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
};

// Legacy-карта склеивает отказ и недостижение цели в красный, а triage их различает:
// «цель не достигнута» и «клиент отказался» требуют от руководителя разных действий.
const NODE_CALL_OUTCOME_BADGE_CLASSES = {
  [NODE_CALL_OUTCOME_CODES.ACHIEVED]: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
  [NODE_CALL_OUTCOME_CODES.PENDING]: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-300',
  [NODE_CALL_OUTCOME_CODES.NOT_ACHIEVED]: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  [NODE_CALL_OUTCOME_CODES.CANCELLED]: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  [NODE_CALL_OUTCOME_CODES.UNKNOWN]: NEUTRAL_BADGE_CLASS
};

const BookingOutcomeBadge = ({ call }) => {
  const outcome = resolveCallOutcome(call);
  if (!outcome) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const classes = outcome.source === 'node'
    ? NODE_CALL_OUTCOME_BADGE_CLASSES[outcome.code]
    : BOOKING_OUTCOME_BADGE_CLASSES[outcome.code];

  return (
    <Badge className={classes ?? NEUTRAL_BADGE_CLASS} title={outcome.reason ?? undefined}>
      {outcome.label}
    </Badge>
  );
};

export default CallDetails;
